import { useEffect, useState, useCallback } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { isSolanaWallet } from "@dynamic-labs/solana";
import type { SolanaWallet } from "@dynamic-labs/solana-core";
import type { Token, TxPhase } from "@/types";
import { TokenAvatar } from "./TokenAvatar";
import { CloseIcon, LoaderIcon, CheckIcon, ArrowRightIcon, ExternalLinkIcon } from "./Icons";
import { GaslessFeeDisplay } from "./GaslessFeeDisplay";
import { fmtNumber, shortAddr, solscanTxUrl } from "@/lib/utils";
import { buildSendTransaction, serializeTransaction } from "@/lib/solana";
import type { Transaction as Web3Transaction } from "@solana/web3.js";
import { getObliqFeePayer, signAndSendTransaction } from "@/lib/obliq";

interface ConfirmSendDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	token: Token | undefined;
	amount: number;
	recipient: string;
	feeSol?: number;
	isFeeLoading?: boolean;
	onSuccess: () => void;
}

export function ConfirmSendDialog({
	open,
	onOpenChange,
	token,
	amount,
	recipient,
	feeSol,
	isFeeLoading = false,
	onSuccess,
}: ConfirmSendDialogProps) {
	const [phase, setPhase] = useState<TxPhase>("review");
	const [txHash, setTxHash] = useState<string>("");
	const [error, setError] = useState<string>("");

	const { primaryWallet } = useDynamicContext();

	useEffect(() => {
		if (open) {
			setPhase("review");
			setTxHash("");
			setError("");
		}
	}, [open]);

	const handleConfirm = useCallback(async () => {
		if (!token || !primaryWallet || !isSolanaWallet(primaryWallet)) return;
		setPhase("signing");
		setError("");

		try {
			const signer = await (primaryWallet as SolanaWallet).getSigner();
			const feePayer = await getObliqFeePayer();
			const tx = await buildSendTransaction(
				primaryWallet.address,
				recipient,
				token.address,
				amount,
				token.decimals,
				feePayer,
			);

			// Dynamic SDK bundles its own @solana/web3.js; types are structurally identical at runtime
			// biome-ignore lint/suspicious/noExplicitAny: duplicate @solana/web3.js versions
			const signedTx = await signer.signTransaction(tx as never);
			setPhase("processing");

			const serialized = serializeTransaction(signedTx as unknown as Web3Transaction);
			const result = await signAndSendTransaction(serialized);

			setTxHash(result.signature);
			setPhase("success");
			onSuccess();
		} catch (err) {
			const msg = err instanceof Error ? err.message : "Transaction failed";
			setError(msg);
			setPhase("error");
		}
	}, [token, primaryWallet, recipient, amount, onSuccess]);

	if (!token) return null;

	const isLoading = phase === "signing" || phase === "processing";
	const usdValue = amount * token.usd;

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="dialog-overlay" />
				<Dialog.Content className="dialog-content" style={{ maxWidth: 420 }}>
					<div className="dialog-head">
						<Dialog.Title className="dialog-title">Review send</Dialog.Title>
						<Dialog.Close asChild>
							<button className="dialog-close" aria-label="Close" disabled={isLoading}>
								<CloseIcon />
							</button>
						</Dialog.Close>
					</div>

					<div className="confirm-body">
						<div className="confirm-flow">
							<div className="confirm-side">
								<span className="leg-label">Sending</span>
								<span className="leg-amount">{fmtNumber(amount, 6)}</span>
								<span className="leg-tok">
									<TokenAvatar token={token} size={22} />
									{token.symbol} · Solana
								</span>
								<span style={{ fontSize: 11, color: "var(--text-subtle)" }}>
									≈ {fmtNumber(usdValue, 2)} USD
								</span>
							</div>

							<div className="confirm-arrow">
								<ArrowRightIcon />
							</div>

							<div className="confirm-side">
								<span className="leg-label">Recipient gets</span>
								<span className="leg-amount">{fmtNumber(amount, 6)}</span>
								<span className="leg-tok">
									<TokenAvatar token={token} size={22} />
									{token.symbol}
								</span>
								<span
									style={{
										fontSize: 11,
										color: "var(--text-subtle)",
										fontFamily: "JetBrains Mono, monospace",
									}}
								>
									{shortAddr(recipient)}
								</span>
							</div>
						</div>

						<div className="confirm-summary">
							<div className={`ico${phase === "error" ? " ico--error" : ""}`}>
								{isLoading && <LoaderIcon className="spin" />}
								{phase === "success" && <CheckIcon />}
								{phase === "review" && <ArrowRightIcon />}
								{phase === "error" && <span>!</span>}
							</div>
							<div className="text">
								<span className="title">
									{phase === "review" && `Send ${token.symbol}`}
									{phase === "signing" && "Awaiting signature…"}
									{phase === "processing" && "Processing transaction…"}
									{phase === "success" && "Transaction submitted!"}
									{phase === "error" && "Transaction failed"}
								</span>
								<span className={`sub${phase === "error" ? " sub--error" : ""}`}>
									{phase === "review" && "Confirm in your wallet"}
									{phase === "signing" && "Approve in your wallet app"}
									{phase === "processing" && "Submitting via OBLIQ…"}
									{phase === "success" && txHash && (
										<a
											href={solscanTxUrl(txHash)}
											target="_blank"
											rel="noopener noreferrer"
											className="solscan-link"
										>
											View on Solscan <ExternalLinkIcon />
										</a>
									)}
									{phase === "error" && (error || "Please try again")}
								</span>
							</div>
						</div>

						<div className="confirm-fee-rows">
							<FeeRow label="Network fee">
								<GaslessFeeDisplay feeSol={feeSol} isLoading={isFeeLoading} />
							</FeeRow>
							<FeeRow label="Token" value={token.symbol} />
							<FeeRow label="Network" value="Solana Mainnet" />
						</div>
					</div>

					<div className="confirm-actions">
						<button
							className="btn-secondary"
							onClick={() => onOpenChange(false)}
							disabled={isLoading}
						>
							{phase === "success" ? "Close" : "Cancel"}
						</button>
						{phase !== "success" && phase !== "error" && (
							<button className="btn-confirm" onClick={() => void handleConfirm()} disabled={isLoading}>
								{isLoading ? "Processing…" : "Confirm & Send"}
							</button>
						)}
						{phase === "error" && (
							<button className="btn-confirm" onClick={() => void handleConfirm()}>
								Retry
							</button>
						)}
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

function FeeRow({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
	return (
		<div className="fee-row">
			<span style={{ color: "var(--text-muted)" }}>{label}</span>
			<span style={{ fontWeight: 500 }}>{children ?? value}</span>
		</div>
	);
}
